import { ApiErrorData, ApiResponse } from 'models/ApiResponse';
import {
  TiendaPerfectaAllDistributorsParams,
  TiendaPerfectaCompaniesResponse,
  TiendaPerfectaDistributorsParams,
  TiendaPerfectaDistributorsResponse,
  TiendaPerfectaMyScopeResponse,
  TiendaPerfectaOperatorDetail,
  TiendaPerfectaRouteReport,
  TiendaPerfectaRouteReportParams,
  TiendaPerfectaScopedDistributorsParams,
  TiendaPerfectaStopDetail,
  TiendaPerfectaStoreVisitsInsights,
  TiendaPerfectaVansParams,
  TiendaPerfectaVansResponse,
  TiendaPerfectaVisitHistoryParams,
  TiendaPerfectaVisitHistoryStopsResponse,
  TiendaPerfectaVisitHistorySummary,
} from 'models/TiendaPerfecta';

import {
  adaptVisitHistoryStops,
  adaptVisitHistorySummary,
} from 'services/adapters/visitHistoryAdapter';

import { FunctionsHelper } from 'utils/FunctionsHelper';
import MainBackendAxios from 'utils/MainBackendAxios';

import i18n from '../config/i18n';

const BASE = '/tienda-perfecta';

/**
 * A 2xx whose payload is not the expected shape is an error: adapting it would
 * turn a broken response into a successful, empty report.
 */
const unexpectedResponse = (status: number): ApiResponse<ApiErrorData> => ({
  success: false,
  status,
  data: {
    code: 'UNEXPECTED_RESPONSE',
    message: i18n.t('scenes.visit_history.error.unexpected_response'),
  },
});

/** Listas como `a,b`; vacías o ausentes se omiten (= sin filtro). */
const toVisitHistoryQuery = (params: TiendaPerfectaVisitHistoryParams) => {
  const { zoneIds, distributorIds, ...rest } = params;

  return {
    ...rest,
    ...(zoneIds?.length ? { zoneIds: zoneIds.join(',') } : {}),
    ...(distributorIds?.length
      ? { distributorIds: distributorIds.join(',') }
      : {}),
  };
};

export const TiendaPerfectaService = {
  getMyScope: async (): Promise<
    ApiResponse<TiendaPerfectaMyScopeResponse | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/my-scope`);

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getCompanies: async (): Promise<
    ApiResponse<TiendaPerfectaCompaniesResponse | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/companies`);

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getDistributors: async (
    params: TiendaPerfectaDistributorsParams
  ): Promise<
    ApiResponse<TiendaPerfectaDistributorsResponse | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/distributors`, {
        params,
      });

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getAllDistributors: async (
    params?: TiendaPerfectaAllDistributorsParams
  ): Promise<
    ApiResponse<TiendaPerfectaDistributorsResponse | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/distributors/all`, {
        params,
      });

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getVans: async (
    params: TiendaPerfectaVansParams
  ): Promise<ApiResponse<TiendaPerfectaVansResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/vans`, { params });

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getOperator: async (
    id: string
  ): Promise<ApiResponse<TiendaPerfectaOperatorDetail | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/operators/${id}`);

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  /**
   * El alcance lo resuelve el servidor con la compañía activa y los roles en
   * efecto. No admite `companyCode` ni cabeceras de company: mandarlas está
   * explícitamente prohibido por el contrato.
   */
  getScopedDistributors: async (
    params?: TiendaPerfectaScopedDistributorsParams
  ): Promise<
    ApiResponse<TiendaPerfectaDistributorsResponse | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.get(
        `${BASE}/distributors/scoped`,
        { params }
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  /**
   * El día entero de la company en una sola lectura: contexto, KPIs, avance y
   * las rutas con sus paradas embebidas.
   *
   * `date` es obligatorio y no tiene default: resolver "hoy" en el servidor
   * exigiría conocer antes el timezone de la company, así que dos peticiones
   * idénticas podrían contestar sobre días distintos sin decir cuál.
   */
  getRouteReport: async (
    params: TiendaPerfectaRouteReportParams
  ): Promise<ApiResponse<TiendaPerfectaRouteReport | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/route-report`, {
        params,
      });

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  /**
   * El detalle de una parada: coordenadas, formulario POP con sus respuestas y
   * fotos, materiales e incidencia.
   *
   * El `id` sale del propio reporte (`routes[].stops[].id`). Una parada
   * `pending` responde `200`, no `404`: trae la plantilla completa con los
   * `answer` en `null`.
   *
   * La respuesta NO se cachea: las URLs de las fotos se firman en cada petición
   * y caducan en una hora, así que un detalle guardado sirve imágenes rotas.
   */
  getStop: async (
    id: string
  ): Promise<ApiResponse<TiendaPerfectaStopDetail | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/stops/${id}`);

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  /** Resumen agregado del rango: KPIs, por día, material y camionetas. */
  getVisitHistorySummary: async (
    params: TiendaPerfectaVisitHistoryParams
  ): Promise<ApiResponse<TiendaPerfectaVisitHistorySummary | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(
        `${BASE}/visit-history/summary`,
        { params: toVisitHistoryQuery(params) }
      );

      const raw = response?.data?.data;
      if (!raw || typeof raw !== 'object' || !raw.kpis) {
        return unexpectedResponse(response.status);
      }

      return {
        success: true,
        status: response.status,
        data: adaptVisitHistorySummary(raw),
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  /** Las paradas planas del rango, base de mapa, selección y Excel. */
  getVisitHistoryStops: async (
    params: TiendaPerfectaVisitHistoryParams
  ): Promise<
    ApiResponse<TiendaPerfectaVisitHistoryStopsResponse | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.get(
        `${BASE}/visit-history/stops`,
        { params: toVisitHistoryQuery(params) }
      );

      const raw = response?.data?.data;
      if (!raw || !Array.isArray(raw.stops)) {
        return unexpectedResponse(response.status);
      }

      return {
        success: true,
        status: response.status,
        data: adaptVisitHistoryStops(raw),
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  /** Observaciones y competencia del rango; incompleto mientras el BFF calcula. */
  getStoreVisitInsights: async (
    params: TiendaPerfectaVisitHistoryParams
  ): Promise<ApiResponse<TiendaPerfectaStoreVisitsInsights | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(
        `${BASE}/store-visits/insights`,
        { params: toVisitHistoryQuery(params) }
      );

      const raw = response?.data?.data;
      if (!raw || !raw.progress || !raw.competition || !raw.observations) {
        return unexpectedResponse(response.status);
      }

      return { success: true, status: response.status, data: raw };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
};
